import { Injectable } from '@angular/core';
import { RunView, RunQuery, Metadata, BaseEntity, RunViewParams, RunQueryParams, LogError, LogStatus } from '@memberjunction/core';
import { DataFetchResult, ExtendedRunViewParams } from '../utils/interfaces';

/**
 * Injectable service for data fetching utilities
 * Provides typed, error-handled wrappers for MemberJunction RunView, RunViews, and RunQuery operations
 */
@Injectable({
  providedIn: 'root'
})
export class DataFetchService {

  /**
   * Executes a RunView query for a single entity with comprehensive error handling
   * @param entityName - Name of the entity to query
   * @param extraFilter - Additional filter conditions
   * @param orderBy - Sort order
   * @param resultType - Type of result ('entity_object' or 'simple')
   * @returns Promise resolving to array of typed results
   */
  public async fetchEntityData<T>(
    entityName: string,
    extraFilter: string = '',
    orderBy: string = '',
    resultType: 'entity_object' | 'simple' = 'entity_object'
  ): Promise<T[]> {
    try {
      const runView = new RunView();
      const result = await runView.RunView<T>({
        EntityName: entityName,
        ExtraFilter: extraFilter,
        OrderBy: orderBy,
        ResultType: resultType,
        IgnoreMaxRows: true
      });

      if (!result.Success) {
        LogError(`Failed to fetch ${entityName}:`, undefined, result.ErrorMessage);
        return [];
      }

      return result.Results || [];
    } catch (error) {
      LogError(`Error fetching ${entityName}:`, undefined, error);
      return [];
    }
  }

  /**
   * Executes a RunView query with custom parameters and active record filtering
   * @param params - Extended RunView parameters
   * @returns Promise resolving to typed results with metadata
   */
  public async fetchWithParams<T>(
    params: ExtendedRunViewParams
  ): Promise<DataFetchResult<T[]>> {
    try {
      const runView = new RunView();
      
      // Apply active filter if not explicitly skipped
      let finalFilter = params.ExtraFilter || '';
      if (!params.skipActiveFilter && !params.fetchInactive) {
        const activeFilter = 'IsActive = 1';
        finalFilter = finalFilter 
          ? `(${finalFilter}) AND ${activeFilter}`
          : activeFilter;
      }

      const result = await runView.RunView<T>({
        ...params,
        ExtraFilter: finalFilter,
        IgnoreMaxRows: params.IgnoreMaxRows !== false
      });

      if (!result.Success) {
        return {
          success: false,
          data: [],
          error: result.ErrorMessage || 'Unknown error occurred',
          totalCount: 0
        };
      }

      return {
        success: true,
        data: result.Results || [],
        totalCount: result.TotalRowCount
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      LogError(`Error in fetchWithParams for ${params.EntityName}:`, undefined, error);
      return {
        success: false,
        data: [],
        error: errorMessage,
        totalCount: 0
      };
    }
  }

  /**
   * Executes multiple RunView queries in parallel for optimal performance
   * @param configs - Array of RunView parameters
   * @returns Promise resolving to array of results in the same order as input
   */
  public async fetchMultipleEntities<T = any>(
    configs: RunViewParams[]
  ): Promise<DataFetchResult<T>[]> {
    try {
      const runView = new RunView();
      const enhancedConfigs = configs.map(config => ({
        ...config,
        IgnoreMaxRows: config.IgnoreMaxRows !== false
      }));

      const results = await runView.RunViews<T>(enhancedConfigs);

      return results.map((result, index) => {
        if (!result.Success) {
          LogError(`Failed to fetch ${configs[index].EntityName}:`, undefined, result.ErrorMessage);
          return {
            success: false,
            data: [] as T,
            error: result.ErrorMessage,
            totalCount: 0
          };
        }

        return {
          success: true,
          data: (result.Results || []) as T,
          totalCount: result.TotalRowCount
        };
      });
    } catch (error) {
      LogError('Error in fetchMultipleEntities:', undefined, error);
      return configs.map(() => ({
        success: false,
        data: [] as T,
        error: 'Batch operation failed',
        totalCount: 0
      }));
    }
  }

  /**
   * Executes multiple RunView queries and returns results as a keyed map
   * @param configs - Array of RunView parameters
   * @returns Promise resolving to a map of entity names to their results
   */
  public async fetchEntitiesAsMap<T = any>(
    configs: RunViewParams[]
  ): Promise<Record<string, T[]>> {
    try {
      const runView = new RunView();
      const enhancedConfigs = configs.map(config => ({
        ...config,
        IgnoreMaxRows: config.IgnoreMaxRows !== false
      }));

      const results = await runView.RunViews<T>(enhancedConfigs);
      const resultMap: Record<string, T[]> = {};

      configs.forEach((config, index) => {
        const result = results[index];
        const entityName = config.EntityName;
        
        if (entityName) {
          if (result?.Success && Array.isArray(result.Results)) {
            resultMap[entityName] = result.Results;
          } else {
            LogError(`No results for ${entityName}:`, undefined, result?.ErrorMessage);
            resultMap[entityName] = [];
          }
        }
      });

      return resultMap;
    } catch (error) {
      LogError('Error in fetchEntitiesAsMap:', undefined, error);
      const errorMap: Record<string, T[]> = {};
      configs.forEach(config => {
        if (config.EntityName) {
          errorMap[config.EntityName] = [];
        }
      });
      return errorMap;
    }
  }

  /**
   * Executes a RunQuery operation with comprehensive error handling
   * @param params - Query parameters
   * @returns Promise resolving to typed query results
   */
  public async executeQuery<T>(
    params: RunQueryParams
  ): Promise<DataFetchResult<T[]>> {
    try {
      const runQuery = new RunQuery();
      const result = await runQuery.RunQuery(params);

      if (!result.Success) {
        return {
          success: false,
          data: [],
          error: result.ErrorMessage || 'Query execution failed'
        };
      }

      return {
        success: true,
        data: (result.Results || []) as T[],
        totalCount: result.Results?.length || 0
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      LogError(`Error executing query ${params.QueryName}:`, undefined, error);
      return {
        success: false,
        data: [],
        error: errorMessage
      };
    }
  }

  /**
   * Fetches a single record by ID with strong typing
   * @param entityName - Name of the entity
   * @param recordId - ID of the record to fetch
   * @param resultType - Type of result object
   * @returns Promise resolving to the record or null
   */
  public async fetchRecordById<T>(
    entityName: string,
    recordId: string,
    resultType: 'entity_object' | 'simple' = 'entity_object'
  ): Promise<T | null> {
    try {
      const runView = new RunView();
      const result = await runView.RunView<T>({
        EntityName: entityName,
        ExtraFilter: `ID = '${recordId}'`,
        ResultType: resultType,
        IgnoreMaxRows: true
      });

      if (!result.Success || !result.Results || result.Results.length === 0) {
        return null;
      }

      return result.Results[0];
    } catch (error) {
      LogError(`Error fetching ${entityName} by ID ${recordId}:`, undefined, error);
      return null;
    }
  }

  /**
   * Fetches paginated data with support for offset and limit
   * @param entityName - Name of the entity
   * @param page - Page number (1-based)
   * @param pageSize - Number of records per page
   * @param filter - Optional filter conditions
   * @param orderBy - Sort order
   * @returns Promise resolving to paginated results with metadata
   */
  public async fetchPaginatedData<T>(
    entityName: string,
    page: number = 1,
    pageSize: number = 50,
    filter: string = '',
    orderBy: string = ''
  ): Promise<DataFetchResult<T[]> & { page: number; pageSize: number; totalPages: number }> {
    try {
      const runView = new RunView();
      const offset = (page - 1) * pageSize;
      
      const result = await runView.RunView<T>({
        EntityName: entityName,
        ExtraFilter: filter,
        OrderBy: orderBy,
        ResultType: 'entity_object',
        MaxRows: pageSize,
        StartRow: offset
      });

      if (!result.Success) {
        return {
          success: false,
          data: [],
          error: result.ErrorMessage,
          page,
          pageSize,
          totalPages: 0,
          totalCount: 0
        };
      }

      const totalCount = result.TotalRowCount || 0;
      const totalPages = Math.ceil(totalCount / pageSize);

      return {
        success: true,
        data: result.Results || [],
        totalCount,
        page,
        pageSize,
        totalPages
      };
    } catch (error) {
      LogError(`Error fetching paginated data for ${entityName}:`, undefined, error);
      return {
        success: false,
        data: [],
        error: 'Pagination failed',
        page,
        pageSize,
        totalPages: 0,
        totalCount: 0
      };
    }
  }

  /**
   * Fetches data with automatic retry logic for resilient data loading
   * @param fetchFn - Function that performs the fetch operation
   * @param maxRetries - Maximum number of retry attempts
   * @param retryDelay - Delay between retries in milliseconds
   * @returns Promise resolving to the fetch result
   */
  public async fetchWithRetry<T>(
    fetchFn: () => Promise<T>,
    maxRetries: number = 3,
    retryDelay: number = 1000
  ): Promise<T> {
    let lastError: Error | undefined;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await fetchFn();
      } catch (error) {
        lastError = error instanceof Error ? error : new Error('Unknown error');
        
        if (attempt < maxRetries) {
          LogStatus(`Attempt ${attempt + 1} failed, retrying in ${retryDelay}ms...`);
          await new Promise(resolve => setTimeout(resolve, retryDelay));
        }
      }
    }
    
    throw lastError || new Error('All retry attempts failed');
  }

  /**
   * Creates a new entity instance with proper typing
   * @param entityName - Name of the entity to create
   * @returns Promise resolving to the new entity instance
   */
  public async createEntityInstance<T extends BaseEntity>(
    entityName: string
  ): Promise<T | null> {
    try {
      const metadata = new Metadata();
      const entity = await metadata.GetEntityObject<T>(entityName);
      return entity;
    } catch (error) {
      LogError(`Error creating entity instance for ${entityName}:`, undefined, error);
      return null;
    }
  }

  /**
   * Utility method to build complex filter strings
   * @param conditions - Array of filter conditions
   * @param operator - Logical operator to combine conditions
   * @returns Combined filter string
   */
  public buildFilter(
    conditions: string[],
    operator: 'AND' | 'OR' = 'AND'
  ): string {
    const validConditions = conditions.filter(c => c && c.trim());
    if (validConditions.length === 0) return '';
    if (validConditions.length === 1) return validConditions[0];
    
    return validConditions
      .map(condition => `(${condition})`)
      .join(` ${operator} `);
  }

  /**
   * Checks if an entity has an IsActive field
   * @param entityName - Name of the entity to check
   * @returns Promise resolving to boolean indicating if IsActive field exists
   */
  public async hasActiveField(entityName: string): Promise<boolean> {
    try {
      const metadata = new Metadata();
      const entity = metadata.Entities.find(e => e.Name === entityName);
      if (!entity) return false;
      
      return entity.Fields.some(f => f.Name === 'IsActive');
    } catch (error) {
      LogError(`Error checking IsActive field for ${entityName}:`, undefined, error);
      return false;
    }
  }
}